import { Money } from '@dintero/money';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { default as Alert } from 'antd/es/alert';
import { default as Flex } from 'antd/es/flex';
import type { Dispatch, ComponentProps } from 'react';
import { useEffect, useMemo } from 'react';
import { getCryptoDecimals, isCryptoCode } from 'shared/src/cryptoUtils';

import { formatDate, formatHours } from 'utils/dayjs';
import { formatRenderedCurrency } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import type { CurrencyCode } from 'api';

import { useFxRate } from 'hooks/useFxRate';

import { Spinner } from '@atoms/Spinner/Spinner';

type Props = {
  payCurrencyCode?: CurrencyCode;
  buyCurrencyCode?: CurrencyCode;
  amountToConvert?: string;
  setIsFXConfigured?: Dispatch<boolean>;
} & ComponentProps<'div'>;

export const TransferFXDetails = ({
  payCurrencyCode,
  buyCurrencyCode,
  amountToConvert,
  setIsFXConfigured,
  ...props
}: Props) => {
  const { fx, error, success } = useFxRate({
    payCurrencyCode: payCurrencyCode,
    buyCurrencyCode: buyCurrencyCode
  });

  useEffect(() => {
    setIsFXConfigured?.(success);
  }, [setIsFXConfigured, success]);

  const suggestedTotal = useMemo(() => {
    if (
      !hasValue(amountToConvert) ||
      !hasValue(fx?.reciprocalExchangeRate) ||
      !hasValue(buyCurrencyCode)
    ) {
      return '...';
    }

    const mergeOptions = {
      decimals: isCryptoCode(buyCurrencyCode)
        ? getCryptoDecimals(buyCurrencyCode)
        : 2
    };
    const amount = Number(amountToConvert);

    const calculatedMoney = Money.fromPrice(
      fx!.reciprocalExchangeRate,
      buyCurrencyCode!
    )
      .multiply(amount)
      .merge(mergeOptions)
      .toNumber();

    return formatRenderedCurrency(calculatedMoney, buyCurrencyCode);
  }, [amountToConvert, buyCurrencyCode, fx]);

  if (error) {
    return (
      <Alert
        className="mt-3 w-full border-none text-feedback-error"
        message="Exchange rate is not available for this transaction."
        type="error"
        icon={<ExclamationCircleIcon className="size-5" />}
        showIcon
      />
    );
  }
  return fx ? (
    <div {...props}>
      <p className="text-sm font-medium text-gray-500">
        Suggested exchange rate
      </p>
      <Flex justify="space-between">
        <p className="text-xs font-medium text-gray-500">
          {formatDate(fx.utcTime)} {formatHours(fx.utcTime)}
        </p>

        <p className="text-sm font-semibold">
          1 {fx?.payCurrency} = {fx?.reciprocalExchangeRate} {fx?.buyCurrency}
        </p>
      </Flex>
      <Flex justify="space-between" className="mt-2">
        <p className="text-sm font-medium text-gray-500">Suggested total</p>
        <p className="text-sm font-semibold">{suggestedTotal}</p>
      </Flex>
    </div>
  ) : (
    <Spinner />
  );
};
